import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Sparkline, VendorStatusBadge } from "@/pages/farm/shared";
import RevenueCalendar from "@/pages/farm/RevenueCalendar";

function ConnectStripeBanner({ vendor }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const onboard = async () => {
    setBusy(true); setMsg("");
    try {
      const { data } = await api.post("/vendor/stripe/connect", {
        return_url: window.location.origin + "/vendor",
        refresh_url: window.location.origin + "/vendor",
      });
      if (data.url) { window.location.href = data.url; return; }
      setMsg(data.message || "Stripe Connect not available on this test key.");
    } catch (e) {
      setMsg(e.response?.data?.detail || "Onboarding error.");
    } finally { setBusy(false); }
  };
  if (vendor.stripe_onboarded) {
    return (
      <div className="card-flat p-4 flex items-center justify-between" data-testid="stripe-onboarded">
        <span className="text-sm">✓ Stripe Connect onboarded — payouts active.</span>
      </div>
    );
  }
  return (
    <div className="card-flat p-5" data-testid="stripe-connect-banner">
      <p className="overline">Payouts</p>
      <h3 className="font-display text-2xl mt-1">Connect Stripe to receive payouts</h3>
      <p className="text-sm text-[var(--ff-text-secondary)] mt-2">
        Onboard via Stripe Connect Express (1–3 min) to receive your payouts directly to your bank.
      </p>
      <button onClick={onboard} disabled={busy} className="btn-primary mt-4" data-testid="stripe-connect-btn">
        {busy ? "Loading…" : "Onboard with Stripe"}
      </button>
      {msg && <p className="mt-3 text-xs text-[var(--ff-text-muted)]" data-testid="stripe-connect-msg">{msg}</p>}
    </div>
  );
}

function DropAnalyticsCard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/farm/analytics/drops").then(r => setData(r.data)).catch(e => setErr(e.response?.data?.detail || "Load error"));
  }, []);
  if (err) return null;
  if (!data) return (
    <div className="card-flat p-5" data-testid="drop-analytics-loading">
      <p className="overline">Revenue by drop</p>
      <p className="mt-2 text-sm text-[var(--ff-text-muted)]">Loading…</p>
    </div>
  );
  if (!data.rows.length) return (
    <div className="card-flat p-5" data-testid="drop-analytics-empty">
      <p className="overline">Revenue by drop</p>
      <p className="mt-3 text-sm text-[var(--ff-text-muted)]">No drops yet — once a drop ships, you'll see per-drop revenue, sell-through, and waitlist conversion here.</p>
    </div>
  );
  return (
    <div className="card-flat p-5" data-testid="drop-analytics-card">
      <p className="overline">Revenue by drop</p>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm" data-testid="drop-analytics-table">
          <thead className="text-left text-[var(--ff-text-muted)] uppercase tracking-wider text-[10px] font-semibold">
            <tr>
              <th className="py-2 pr-3">Drop</th>
              <th className="py-2 px-3 text-right">Revenue</th>
              <th className="py-2 px-3 text-right">Units</th>
              <th className="py-2 px-3 text-right">Sell-through</th>
              <th className="py-2 px-3 text-right">Waitlist</th>
              <th className="py-2 pl-3 text-right">Conversion</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--ff-border)]">
            {data.rows.map(r => (
              <tr key={r.slug} data-testid={`drop-an-${r.slug}`}>
                <td className="py-3 pr-3">
                  <p className="font-medium">{r.name}</p>
                  <p className="text-[11px] text-[var(--ff-text-muted)]">{r.drops_at?.slice(0,10)} · ${r.price}</p>
                </td>
                <td className="py-3 px-3 text-right font-medium">${r.revenue.toFixed(2)}</td>
                <td className="py-3 px-3 text-right">{r.units_sold}</td>
                <td className="py-3 px-3 text-right">
                  <span className="inline-block min-w-[3rem]">{r.sellthrough_pct}%</span>
                  <span className="inline-block w-16 h-1.5 ml-2 bg-stone-200 rounded-full align-middle overflow-hidden">
                    <span className="block h-full bg-[var(--ff-primary)]" style={{width: `${Math.min(100, r.sellthrough_pct)}%`}}/>
                  </span>
                </td>
                <td className="py-3 px-3 text-right">{r.waitlist_size}</td>
                <td className="py-3 pl-3 text-right">{r.waitlist_conversion_pct == null ? "—" : `${r.waitlist_conversion_pct}%`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function OverviewTab({ me, overview }) {
  const s = overview.stats;
  const vstatus = me.vendor.vendor_status;
  return (
    <div className="space-y-6" data-testid="farm-overview-tab">
      {vstatus && vstatus !== "approved" && (
        <div className={`card-flat p-5 border-l-4 ${vstatus === "suspended" ? "border-rose-500" : "border-amber-500"}`} data-testid="vendor-status-banner">
          <div className="flex items-center gap-3 flex-wrap">
            <p className="font-display text-lg">Farm status</p>
            <VendorStatusBadge status={vstatus}/>
          </div>
          <p className="text-sm text-[var(--ff-text-secondary)] mt-2">
            {vstatus === "pending_invite" && "Welcome! Head to the Profile tab to finish your farm story and submit for review."}
            {vstatus === "profile_submitted" && "Your profile is under review — our team typically responds within 1 business day."}
            {vstatus === "suspended" && "This farm is currently suspended. Contact platform support to resolve."}
          </p>
        </div>
      )}
      <ConnectStripeBanner vendor={me.vendor} />
      <div className="grid md:grid-cols-4 gap-4" data-testid="farm-stats">
        {[
          ["Revenue", `$${s.revenue.toFixed(2)}`],
          ["Net payout", `$${s.payout.toFixed(2)}`],
          ["Orders", s.orders],
          ["Items sold", s.items_sold],
        ].map(([k,v], i) => (
          <div key={i} className="card-flat p-5" data-testid={`farm-stat-${i}`}>
            <p className="overline">{k}</p>
            <p className="font-display text-3xl mt-2">{v}</p>
          </div>
        ))}
      </div>
      <div className="grid md:grid-cols-3 gap-4" data-testid="farm-pending">
        <div className="card-flat p-5 border-l-4 border-amber-500">
          <p className="overline">Pending pack</p>
          <p className="font-display text-4xl mt-2 text-amber-900" data-testid="pending-pack">{s.pending_pack}</p>
          <p className="text-xs text-[var(--ff-text-muted)] mt-1">Orders waiting for you to pack</p>
        </div>
        <div className="card-flat p-5 border-l-4 border-sky-500">
          <p className="overline">Pending ship</p>
          <p className="font-display text-4xl mt-2 text-sky-900" data-testid="pending-ship">{s.pending_ship}</p>
          <p className="text-xs text-[var(--ff-text-muted)] mt-1">Packed and awaiting carrier pickup</p>
        </div>
        <div className="card-flat p-5">
          <p className="overline">Catalog</p>
          <p className="font-display text-2xl mt-2">{s.drops} drops · {s.products} products</p>
          <p className="text-xs text-[var(--ff-text-muted)] mt-1">Active SKUs across your farm</p>
        </div>
      </div>
      <div className="card-flat p-5" data-testid="farm-trend">
        <p className="overline">Revenue · last 30 days</p>
        <div className="mt-3"><Sparkline series={overview.revenue_series}/></div>
      </div>
      <RevenueCalendar/>
      <DropAnalyticsCard/>
    </div>
  );
}
